import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { AiOutlineAudio, AiOutlineAudioMuted } from "react-icons/ai";
import { LuCamera, LuSparkles, LuVideo, LuX } from "react-icons/lu";
import {
    createBlogPost,
    enhanceBlogBody,
    fetchBlogImageUrl,
    fetchBlogPost,
    fetchBlogPresign,
    updateBlogPost,
} from "../../utils/apiUtils";
import { appendSpokenToBody, blogHomePath, hasBlogBody } from "./blogShared.jsx";
import { bodyWithLegacyImages, extractS3Keys, mergeWriterDraft, stripPendingPreview } from "./blogMedia.js";
import { clearComposeDraft, readComposeDraft, writeComposeDraft } from "./blogComposeDraft.js";
import BlogBodyEditor from "./BlogBodyEditor.jsx";
import BlogWriter from "./BlogWriter.jsx";
import { useBlogWalkthrough } from "./useBlogWalkthrough.js";
import "./Blog.scss";

const VIDEO_MAX_MS = 60_000;

const pickRecorderType = () => {
    if (typeof MediaRecorder === "undefined") return "";
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")) return "video/webm;codecs=vp9,opus";
    if (MediaRecorder.isTypeSupported("video/webm")) return "video/webm";
    if (MediaRecorder.isTypeSupported("video/mp4")) return "video/mp4";
    return "";
};

const formatClipTime = (ms) => {
    const secs = Math.min(60, Math.max(0, Math.floor(ms / 1000)));
    return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
};

const flagLabel = {
    phi: "protected health information",
    pii: "personal information",
    foul: "language we cannot publish",
};

const BlogCompose = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const editing = Boolean(id);
    const inputRef = useRef(null);
    const recRef = useRef(null);
    const listeningRef = useRef(false);
    const bodyRef = useRef("");
    const bodyEditorRef = useRef(null);
    const postIdRef = useRef(id);
    const liveVideoRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const tickRef = useRef(null);
    const recordingRef = useRef(false);
    const photoVideoRef = useRef(null);
    const photoStreamRef = useRef(null);
    const restored = !id ? readComposeDraft(null) : null;
    const persistReadyRef = useRef(false);
    const clearWriterRef = useRef(null);
    const [title, setTitle] = useState(restored?.title || "");
    const [body, setBody] = useState(restored?.body || "");
    const [originalBody, setOriginalBody] = useState(restored?.originalBody || "");
    const [over, setOver] = useState(false);
    const [photoLive, setPhotoLive] = useState(false);
    const [busy, setBusy] = useState(false);
    const [enhancing, setEnhancing] = useState(false);
    const [listening, setListening] = useState(false);
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [videoFile, setVideoFile] = useState(null);
    const [keptVideoKey, setKeptVideoKey] = useState(null);
    const [videoPreview, setVideoPreview] = useState("");
    const [error, setError] = useState("");
    const [drafting, setDrafting] = useState(false);
    const hydratedRef = useRef("");
    const [editorReady, setEditorReady] = useState(!editing);

    bodyRef.current = body;
    useBlogWalkthrough("compose", { ready: editorReady });

    useEffect(() => {
        if (!persistReadyRef.current) return;
        if (editing && !hydratedRef.current) return;
        const timer = setTimeout(() => {
            writeComposeDraft(id, { title, body, originalBody });
        }, 200);
        const flush = () => {
            if (!persistReadyRef.current) return;
            writeComposeDraft(id, { title, body, originalBody });
        };
        const onHide = () => {
            if (document.visibilityState === "hidden") flush();
        };
        window.addEventListener("beforeunload", flush);
        document.addEventListener("visibilitychange", onHide);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("beforeunload", flush);
            document.removeEventListener("visibilitychange", onHide);
            flush();
        };
    }, [id, title, body, originalBody, editing]);

    const existing = useQuery({
        queryKey: ["blog-post", id],
        queryFn: () => fetchBlogPost(id),
        enabled: editing,
    });

    useEffect(() => {
        hydratedRef.current = "";
        persistReadyRef.current = false;
        setEditorReady(!id);
        if (!id) {
            const local = readComposeDraft(null);
            setTitle(local?.title || "");
            setBody(local?.body || "");
            setOriginalBody(local?.originalBody || "");
        }
    }, [id]);

    useEffect(() => {
        if (editing && !hydratedRef.current) return;
        if (!editorReady) return;
        const timer = setTimeout(() => {
            persistReadyRef.current = true;
        }, 80);
        return () => clearTimeout(timer);
    }, [id, editing, editorReady]);

    useEffect(() => {
        const post = existing.data;
        if (!post || hydratedRef.current === post.id) return;
        if (!post.mine) {
            navigate(`/blog/${post.id}`, { replace: true });
            return;
        }
        hydratedRef.current = post.id;
        postIdRef.current = post.id;
        const keys = post.imageKeys?.length ? post.imageKeys : (post.imageKey ? [post.imageKey] : []);
        const serverBody = bodyWithLegacyImages(post.body || "", keys);
        const local = readComposeDraft(post.id);
        if (local && (local.title.trim() || hasBlogBody(local.body))) {
            setTitle(local.title || post.title || "");
            setBody(local.body || serverBody);
            setOriginalBody(local.originalBody || "");
        } else {
            setTitle(post.title || "");
            setBody(serverBody);
        }
        setEditorReady(true);
        if (post.videoKey) {
            setKeptVideoKey(post.videoKey);
            fetchBlogImageUrl(post.videoKey).then((result) => {
                setVideoPreview(result?.url || "");
            });
        }
    }, [existing.data, navigate]);

    useEffect(() => {
        const Speech = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!Speech) return undefined;
        const rec = new Speech();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = "en-US";
        rec.onresult = (event) => {
            let chunk = "";
            for (let i = event.resultIndex; i < event.results.length; i += 1) {
                if (event.results[i].isFinal) {
                    chunk += event.results[i][0].transcript;
                }
            }
            const spoken = chunk.trim();
            if (!spoken) return;
            setBody((prev) => appendSpokenToBody(prev, spoken));
        };
        rec.onerror = () => {
            listeningRef.current = false;
            setListening(false);
        };
        rec.onend = () => {
            if (!listeningRef.current) {
                setListening(false);
                return;
            }
            try {
                rec.start();
            } catch {
                listeningRef.current = false;
                setListening(false);
            }
        };
        recRef.current = rec;
        return () => {
            listeningRef.current = false;
            try {
                rec.stop();
            } catch {
                // already stopped
            }
        };
    }, []);

    const toggleMic = () => {
        const rec = recRef.current;
        if (!rec) {
            setError("Voice input is not supported in this browser.");
            return;
        }
        if (listeningRef.current) {
            listeningRef.current = false;
            setListening(false);
            try {
                rec.stop();
            } catch {
                // already stopped
            }
            return;
        }
        setError("");
        listeningRef.current = true;
        setListening(true);
        try {
            rec.start();
        } catch {
            listeningRef.current = false;
            setListening(false);
            setError("Could not start the microphone.");
        }
    };

    const onEnhance = async () => {
        if (!hasBlogBody(body) || enhancing) return;
        setError("");
        setEnhancing(true);
        try {
            const result = await enhanceBlogBody({ title, body: stripPendingPreview(body) });
            if (result?.body) {
                if (!originalBody) setOriginalBody(body);
                setBody(result.body);
            }
        } catch (err) {
            setError(err.message || "Could not enhance");
        } finally {
            setEnhancing(false);
        }
    };

    const onReset = () => {
        if (!originalBody) return;
        setBody(originalBody);
        setOriginalBody("");
    };

    const clearForm = () => {
        setTitle("");
        setBody("");
        setOriginalBody("");
        setError("");
        setDrafting(false);
        bodyEditorRef.current?.setHtml?.("");
        writeComposeDraft(id, { title: "", body: "", originalBody: "" });
    };

    const takeVideoFile = (next) => {
        if (!next) return;
        if (!next.type.startsWith("video/")) {
            setError("Choose a video file.");
            return;
        }
        if (recordingRef.current) stopRecording();
        setError("");
        if (videoPreview?.startsWith("blob:")) URL.revokeObjectURL(videoPreview);
        setKeptVideoKey(null);
        setVideoFile(next);
        setVideoPreview(URL.createObjectURL(next));
        setElapsed(0);
    };

    const clearVideo = () => {
        if (videoPreview?.startsWith("blob:")) URL.revokeObjectURL(videoPreview);
        setKeptVideoKey(null);
        setVideoFile(null);
        setVideoPreview("");
        setElapsed(0);
        if (inputRef.current) inputRef.current.value = "";
    };

    const isImageFile = (file) => Boolean(file?.type?.startsWith("image/"));
    const isVideoFile = (file) => Boolean(file?.type?.startsWith("video/"));

    const uploadBodyImage = async (file) => {
        const safeName = String(file.name || "image.jpg").replace(/[^\w.\-]/g, "") || "image.jpg";
        const presign = await fetchBlogPresign({
            fileName: `${Date.now()}-${safeName}`,
            contentType: file.type || "image/png",
            postId: postIdRef.current,
        });
        postIdRef.current = presign.postId;
        if (presign.url) {
            const uploaded = await fetch(presign.url, {
                method: "PUT",
                headers: { "Content-Type": file.type || "image/png" },
                body: file,
            });
            if (!uploaded.ok) throw new Error("Image upload failed");
        }
        return { s3Key: presign.s3Key };
    };

    const takeMediaFiles = (list) => {
        const incoming = [...(list || [])].filter(Boolean);
        if (!incoming.length) return;
        const imagesIn = incoming.filter(isImageFile);
        const videosIn = incoming.filter(isVideoFile);
        if (!imagesIn.length && !videosIn.length) {
            setError("Choose an image or video file.");
            return;
        }
        if (videosIn.length) takeVideoFile(videosIn[0]);
        if (imagesIn.length) bodyEditorRef.current?.insertFiles(imagesIn);
        if (inputRef.current) inputRef.current.value = "";
    };

    const stopCamera = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (liveVideoRef.current) liveVideoRef.current.srcObject = null;
    };

    const stopRecording = () => {
        recordingRef.current = false;
        clearInterval(tickRef.current);
        tickRef.current = null;
        setRecording(false);
        try {
            if (recorderRef.current && recorderRef.current.state !== "inactive") {
                recorderRef.current.stop();
            }
        } catch {
            // already stopped
        }
        stopCamera();
    };

    const startRecording = async () => {
        if (recordingRef.current || busy) return;
        stopPhotoCamera();
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            setError("Video recording is not supported in this browser.");
            return;
        }
        setError("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            streamRef.current = stream;
            if (liveVideoRef.current) {
                liveVideoRef.current.srcObject = stream;
            }
            const mimeType = pickRecorderType();
            const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            chunksRef.current = [];
            rec.ondataavailable = (event) => {
                if (event.data?.size) chunksRef.current.push(event.data);
            };
            rec.onstop = () => {
                const type = rec.mimeType || "video/webm";
                const ext = type.includes("mp4") ? "mp4" : "webm";
                const blob = new Blob(chunksRef.current, { type: type.split(";")[0] });
                if (!blob.size) return;
                if (videoPreview) URL.revokeObjectURL(videoPreview);
                const file = new File([blob], `clip.${ext}`, { type: blob.type });
                setVideoFile(file);
                setVideoPreview(URL.createObjectURL(blob));
            };
            recorderRef.current = rec;
            recordingRef.current = true;
            setElapsed(0);
            setRecording(true);
            rec.start();
            const started = Date.now();
            tickRef.current = setInterval(() => {
                const ms = Date.now() - started;
                setElapsed(ms);
                if (ms >= VIDEO_MAX_MS) stopRecording();
            }, 200);
        } catch {
            stopCamera();
            recordingRef.current = false;
            setRecording(false);
            setError("Could not start the camera.");
        }
    };

    useEffect(() => {
        if (recording && liveVideoRef.current && streamRef.current) {
            liveVideoRef.current.srcObject = streamRef.current;
        }
    }, [recording]);

    const stopPhotoCamera = () => {
        photoStreamRef.current?.getTracks().forEach((track) => track.stop());
        photoStreamRef.current = null;
        if (photoVideoRef.current) photoVideoRef.current.srcObject = null;
        setPhotoLive(false);
    };

    const startPhotoCamera = async () => {
        if (photoLive || busy) return;
        if (!navigator.mediaDevices?.getUserMedia) {
            setError("Photo capture is not supported in this browser.");
            return;
        }
        if (recordingRef.current) stopRecording();
        setError("");
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            photoStreamRef.current = stream;
            setPhotoLive(true);
        } catch {
            stopPhotoCamera();
            setError("Could not start the camera.");
        }
    };

    const snapPhoto = () => {
        const video = photoVideoRef.current;
        if (!video) return;
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 1280;
        canvas.height = video.videoHeight || 720;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
            if (!blob) {
                setError("Could not capture the photo.");
                return;
            }
            bodyEditorRef.current?.insertFiles([new File([blob], "photo.jpg", { type: "image/jpeg" })]);
            stopPhotoCamera();
        }, "image/jpeg", 0.92);
    };

    useEffect(() => {
        if (photoLive && photoVideoRef.current && photoStreamRef.current) {
            photoVideoRef.current.srcObject = photoStreamRef.current;
        }
    }, [photoLive]);

    useEffect(() => () => {
        recordingRef.current = false;
        clearInterval(tickRef.current);
        try {
            recorderRef.current?.stop();
        } catch {
            // already stopped
        }
        stopCamera();
        stopPhotoCamera();
    }, []);

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!hasBlogBody(body)) {
            setError("Write a body before publishing.");
            return;
        }
        setError("");
        setBusy(true);
        try {
            if (recordingRef.current) stopRecording();
            if (photoLive) stopPhotoCamera();
            let videoKey = videoFile ? null : keptVideoKey;
            let postId = postIdRef.current || (editing ? id : undefined);
            if (videoFile) {
                const presign = await fetchBlogPresign({
                    fileName: videoFile.name,
                    contentType: videoFile.type || "video/webm",
                    postId,
                });
                postId = presign.postId;
                postIdRef.current = postId;
                videoKey = presign.s3Key;
                if (presign.url) {
                    const uploaded = await fetch(presign.url, {
                        method: "PUT",
                        headers: { "Content-Type": videoFile.type || "video/webm" },
                        body: videoFile,
                    });
                    if (!uploaded.ok) throw new Error("Video upload failed");
                }
            }
            const published = await bodyEditorRef.current?.publishImages(uploadBodyImage);
            const nextBody = published?.html || body;
            const imageKeys = published?.imageKeys || extractS3Keys(nextBody);
            const imageKey = imageKeys[0] || null;
            const payload = { title, body: nextBody, imageKey, imageKeys, videoKey, postId };
            const post = editing
                ? await updateBlogPost(id, payload)
                : await createBlogPost(payload);
            clearComposeDraft(id);
            navigate(`/blog/${post.id}`);
        } catch (err) {
            const flags = err.cause?.flags;
            if (flags?.length) {
                setError(`Cannot publish: ${flags.map((f) => flagLabel[f] || f).join(", ")}.`);
            } else {
                setError(err.message || "Could not publish");
            }
        } finally {
            setBusy(false);
        }
    };

    if (editing && existing.isPending) {
        return (
            <div className="container blog blog--page scrollable">
                <div className="blog__sheet">
                    <p className="blog__status">Loading…</p>
                </div>
            </div>
        );
    }

    if (editing && existing.error) {
        return (
            <div className="container blog blog--page scrollable">
                <div className="blog__sheet">
                    <p className="blog__error">{existing.error.message || "Post not found"}</p>
                </div>
            </div>
        );
    }

        return (
            <div className="container blog blog--page blog--compose scrollable">
                <div className="blog__sheet">
            <p className="title">{editing ? "Edit post" : "Write a post"}</p>
            <p className="sub-title">
                Keep it operational. We check for PHI, PII, and foul language before it goes up.
            </p>

            <form className="blog__form" onSubmit={onSubmit}>
                <label className="blog__field" data-blog-tour="title">
                    <span>Title</span>
                    <span className={`blog__title-wrap${drafting ? " is-drafting" : ""}`}>
                        <input
                            required
                            placeholder="What is this about?"
                            value={title}
                            disabled={drafting}
                            onChange={(event) => setTitle(event.target.value)}
                        />
                        {drafting ? (
                            <span className="blog__draft-veil blog__draft-veil--title" aria-live="polite">
                                <span className="blog__draft-spin" aria-hidden="true" />
                                Working on it…
                            </span>
                        ) : null}
                    </span>
                </label>
                <div className="blog__field" data-blog-tour="media">
                    <span>Video</span>
                    <div
                        className={`blog__drop${over ? " is-over" : ""}`}
                        onDragOver={(event) => {
                            event.preventDefault();
                            setOver(true);
                        }}
                        onDragLeave={() => setOver(false)}
                        onDrop={(event) => {
                            event.preventDefault();
                            setOver(false);
                            takeMediaFiles(event.dataTransfer.files);
                        }}
                    >
                        {recording ? (
                            <video
                                ref={liveVideoRef}
                                className="blog__video-preview"
                                autoPlay
                                muted
                                playsInline
                            />
                        ) : null}
                        {!recording && videoPreview ? (
                            <div className="blog__thumbs">
                                <div className="blog__thumb">
                                    <video className="blog__video-preview" src={videoPreview} controls playsInline />
                                    <button
                                        type="button"
                                        className="blog__thumb-remove"
                                        onClick={clearVideo}
                                        aria-label="Remove video"
                                    >
                                        <LuX size="1rem" />
                                    </button>
                                </div>
                            </div>
                        ) : null}
                        {!recording && !videoPreview ? (
                            <p>Drop a short clip here, or record one.</p>
                        ) : null}
                        <div className="blog__drop-actions">
                            <input
                                ref={inputRef}
                                type="file"
                                accept="video/*"
                                hidden
                                onChange={(event) => takeMediaFiles(event.target.files)}
                            />
                            {recording ? (
                                <button
                                    type="button"
                                    className="blog__btn blog__btn--ghost"
                                    onClick={stopRecording}
                                    disabled={busy}
                                >
                                    <LuVideo size="0.95rem" />
                                    Stop video
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="blog__btn blog__btn--ghost"
                                        onClick={startRecording}
                                        disabled={busy}
                                    >
                                        <LuVideo size="0.95rem" />
                                        {videoPreview ? "Re-record" : "Record video"}
                                    </button>
                                    <button
                                        type="button"
                                        className="blog__btn blog__btn--ghost"
                                        onClick={() => inputRef.current?.click()}
                                        disabled={busy}
                                    >
                                        {videoFile || keptVideoKey ? "Replace video" : "Choose video"}
                                    </button>
                                    {videoFile || keptVideoKey ? (
                                        <button
                                            type="button"
                                            className="blog__btn blog__btn--text"
                                            onClick={clearVideo}
                                        >
                                            Remove video
                                        </button>
                                    ) : null}
                                </>
                            )}
                        </div>
                        <p className={`blog__media-hint${recording ? " is-rec" : ""}`}>
                            {recording
                                ? `${formatClipTime(elapsed)} / 1:00`
                                : videoFile
                                    ? videoFile.name
                                    : keptVideoKey
                                        ? "Video attached"
                                        : "MP4, WebM, or MOV · record 1 minute"}
                        </p>
                    </div>
                </div>

                <div className="blog__field" data-blog-tour="body">
                    <span>Body</span>
                    <p className="blog__field-hint">
                        Headings, lists, tables, images, and links — format as you write.
                    </p>
                    {photoLive ? (
                        <div className="blog__photo-live">
                            <video
                                ref={photoVideoRef}
                                className="blog__video-preview"
                                autoPlay
                                muted
                                playsInline
                            />
                            <div className="blog__drop-actions">
                                <button
                                    type="button"
                                    className="blog__btn blog__btn--ghost"
                                    onClick={snapPhoto}
                                    disabled={busy}
                                >
                                    <LuCamera size="0.95rem" />
                                    Take photo
                                </button>
                                <button
                                    type="button"
                                    className="blog__btn blog__btn--text"
                                    onClick={stopPhotoCamera}
                                >
                                    Cancel
                                </button>
                            </div>
                            <p className="blog__media-hint">Line up the shot — it goes in the body.</p>
                        </div>
                    ) : null}
                    <div className={`blog__body-wrap${drafting ? " is-drafting" : ""}`}>
                        {editorReady ? (
                            <BlogBodyEditor
                                ref={bodyEditorRef}
                                value={body}
                                onChange={setBody}
                                onError={setError}
                                onStartCapture={startPhotoCamera}
                                capturing={photoLive}
                                disabled={busy || drafting}
                            />
                        ) : (
                            <p className="blog__status">Loading editor…</p>
                        )}
                        {drafting ? (
                            <div className="blog__draft-veil" aria-live="polite">
                                <span className="blog__draft-spin" aria-hidden="true" />
                                Working on it…
                            </div>
                        ) : null}
                        <button
                            type="button"
                            className={`blog__mic${listening ? " is-on" : ""}`}
                            onClick={toggleMic}
                            disabled={drafting}
                            aria-pressed={listening}
                            aria-label={listening ? "Stop recording" : "Start recording"}
                            title={listening ? "Stop recording" : "Start recording"}
                        >
                            {listening ? <AiOutlineAudioMuted size="1.25rem" /> : <AiOutlineAudio size="1.25rem" />}
                        </button>
                    </div>
                    <div className="blog__body-tools">
                        <button
                            type="button"
                            className="blog__ai-btn"
                            onClick={onEnhance}
                            disabled={!hasBlogBody(body) || enhancing || busy || drafting}
                        >
                            <LuSparkles size="0.95rem" />
                            {enhancing ? "Enhancing…" : "Enhance with AI"}
                        </button>
                        <button
                            type="button"
                            className="blog__btn blog__btn--text"
                            onClick={onReset}
                            disabled={!originalBody || enhancing || busy || drafting}
                        >
                            Reset
                        </button>
                    </div>
                </div>

                {error ? <p className="blog__error">{error}</p> : null}
                <div className="blog__actions">
                    <Link to={blogHomePath()} className="blog__btn blog__btn--ghost">Cancel</Link>
                    <button
                        type="button"
                        className="blog__btn blog__btn--danger"
                        disabled={busy || drafting || recording || photoLive}
                        onClick={() => clearWriterRef.current?.()}
                    >
                        Clear
                    </button>
                    <button type="submit" className="blog__btn blog__btn--primary" disabled={busy || drafting || recording || photoLive || !hasBlogBody(body)}>
                        {busy ? "Checking…" : editing ? "Save" : "Publish"}
                    </button>
                </div>
            </form>
            </div>
            <BlogWriter
                draftId={id}
                title={title}
                body={stripPendingPreview(body)}
                disabled={busy}
                clearRef={clearWriterRef}
                onClear={clearForm}
                onDrafting={setDrafting}
                onDraft={(draft) => {
                    if (draft.title) setTitle(draft.title);
                    if (draft.body) {
                        const next = mergeWriterDraft(bodyRef.current, draft.body);
                        setBody(next);
                        bodyEditorRef.current?.setHtml?.(next);
                    }
                    setDrafting(false);
                }}
            />
        </div>
    );
};

export default BlogCompose;
